import { supabase } from "@/integrations/supabase/client";
import type { Payment, CreatePaymentSession, PaymentSessionResponse } from "@/types/payments";
import { scheduleAutomation } from "@/services/automationService";

export const getPaymentsByReservation = async (reservationId: string): Promise<Payment[]> => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('reservation_id', reservationId)
    .order('due_date', { ascending: true });
    
  if (error) throw error;
  return data as Payment[];
};

export const getAllPayments = async (): Promise<Payment[]> => {
  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      reservations!inner (
        first_name,
        last_name,
        email,
        check_in,
        check_out,
        deleted_at
      )
    `)
    .is('reservations.deleted_at', null)
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  return data as Payment[];
};

export const createPaymentRecords = async (reservationId: string): Promise<Payment[]> => {
  // Vérifier d'abord s'il existe déjà des paiements pour cette réservation
  const { data: existingPayments, error: checkError } = await supabase
    .from('payments')
    .select('id')
    .eq('reservation_id', reservationId);
    
  if (checkError) throw checkError;
  
  // Si des paiements existent déjà, les retourner sans en créer de nouveaux
  if (existingPayments && existingPayments.length > 0) {
    console.log('Paiements déjà existants pour la réservation:', reservationId);
    const { data: payments, error } = await supabase
      .from('payments')
      .select('*')
      .eq('reservation_id', reservationId)
      .order('due_date', { ascending: true });
    
    if (error) throw error;
    return payments as Payment[];
  }

  // Récupérer les détails de la réservation
  const { data: reservation, error: reservationError } = await supabase
    .from('reservations')
    .select('*')
    .eq('id', reservationId)
    .single();
    
  if (reservationError) throw reservationError;
  
  // Ne pas créer de paiements si la source n'est pas Direct ou PAP
  if (reservation.source !== 'Direct' && reservation.source !== 'PAP') {
    console.log('Pas de création de paiements pour la source:', reservation.source);
    return [];
  }
  
  const totalPrice = reservation.total_price;
  const checkInDate = new Date(reservation.check_in);
  const now = new Date();
  const daysUntilCheckIn = Math.ceil((checkInDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  console.log('Création des paiements pour réservation:', reservationId, 'Jours jusqu\'arrivée:', daysUntilCheckIn);
  
  const paymentsToCreate: any[] = [];

  if (daysUntilCheckIn > 30) {
    // Plus de 30 jours : paiement en 2 fois (30% + 70%)
    const depositAmount = Math.round(totalPrice * 0.3);
    const finalAmount = totalPrice - depositAmount;
    const finalPaymentDate = new Date(checkInDate);
    finalPaymentDate.setMonth(finalPaymentDate.getMonth() - 1);

    paymentsToCreate.push({
      reservation_id: reservationId,
      payment_type: 'deposit',
      amount: depositAmount,
      status: 'pending',
      payment_method: 'stripe',
      due_date: now.toISOString().split('T')[0],
      stripe_session_id: null,
      stripe_payment_intent_id: null,
      paid_at: null,
      notes: null,
      created_at: now.toISOString(),
      updated_at: now.toISOString()
    });

    paymentsToCreate.push({
      reservation_id: reservationId,
      payment_type: 'final',
      amount: finalAmount,
      status: 'pending',
      payment_method: 'stripe',
      due_date: finalPaymentDate.toISOString().split('T')[0],
      stripe_session_id: null,
      stripe_payment_intent_id: null,
      paid_at: null,
      notes: null,
      created_at: now.toISOString(),
      updated_at: now.toISOString()
    });
  } else {
    // Moins de 30 jours : paiement intégral
    paymentsToCreate.push({
      reservation_id: reservationId,
      payment_type: 'full',
      amount: totalPrice,
      status: 'pending',
      payment_method: 'stripe',
      due_date: now.toISOString().split('T')[0],
      stripe_session_id: null,
      stripe_payment_intent_id: null,
      paid_at: null,
      notes: null,
      created_at: now.toISOString(),
      updated_at: now.toISOString()
    });
  }

  // Caution bancaire avec autorisation étendue (7 jours avant l'arrivée) - MONTANT CHANGÉ À 2000€
  const depositGuaranteeDueDate = new Date(checkInDate);
  depositGuaranteeDueDate.setDate(depositGuaranteeDueDate.getDate() - 7);
  
  paymentsToCreate.push({
    reservation_id: reservationId,
    payment_type: 'deposit_guarantee',
    amount: 2000, // Changé de 500€ à 2000€
    status: 'pending',
    payment_method: 'stripe',
    due_date: depositGuaranteeDueDate.toISOString().split('T')[0],
    stripe_session_id: null,
    stripe_payment_intent_id: null,
    paid_at: null,
    notes: 'Caution bancaire - Autorisation étendue 30 jours',
    created_at: now.toISOString(),
    updated_at: now.toISOString()
  });

  console.log('Création de', paymentsToCreate.length, 'paiements:', paymentsToCreate.map(p => `${p.payment_type}: ${p.amount}€`));

  const { data, error } = await supabase
    .from('payments')
    .insert(paymentsToCreate)
    .select();
    
  if (error) {
    console.error('Erreur lors de la création des paiements:', error);
    throw error;
  }
  
  console.log('Paiements créés avec succès:', data);
  return data as Payment[];
};

export const createPaymentSession = async (params: CreatePaymentSession): Promise<PaymentSessionResponse> => {
  const { data, error } = await supabase.functions.invoke('create-payment-session', {
    body: params
  });
  
  if (error) throw error;
  return data;
};

const sendPaymentConfirmationEmail = async (paymentId: string): Promise<void> => {
  try {
    console.log('Envoi de l\'email de confirmation de paiement pour:', paymentId);
    await supabase.functions.invoke('send-payment-confirmation', {
      body: { payment_id: paymentId }
    });
    console.log('Email de confirmation de paiement envoyé avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'envoi de l\'email de confirmation de paiement:', error);
    // Ne pas faire échouer l'opération principale si l'email échoue
  }
};

export const markPaymentAsPaid = async (
  paymentId: string, 
  paymentMethod: Payment['payment_method'],
  notes?: string
): Promise<Payment> => {
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'paid',
      payment_method: paymentMethod,
      paid_at: new Date().toISOString(),
      notes: notes || null,
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId)
    .select()
    .single();
    
  if (error) throw error;

  // Envoyer l'email de confirmation de paiement
  await sendPaymentConfirmationEmail(paymentId);

  // Generate invoice for this payment
  try {
    console.log('Generating invoice for payment:', paymentId);
    await supabase.functions.invoke('generate-invoice', {
      body: { payment_id: paymentId }
    });
    console.log('Invoice generation triggered successfully');
  } catch (invoiceError) {
    console.error('Error generating invoice:', invoiceError);
    // Don't fail the payment if invoice generation fails
  }

  // Schedule automations for payment_received event
  scheduleAutomation({
    event: 'payment_received',
    targetType: 'reservation',
    targetId: data.reservation_id,
  });

  return data as Payment;
};

export const updatePaymentNotes = async (paymentId: string, notes: string): Promise<Payment> => {
  const { data, error } = await supabase
    .from('payments')
    .update({
      notes,
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId)
    .select()
    .single();
    
  if (error) throw error;
  return data as Payment;
};

export const updatePaymentDueDate = async (paymentId: string, dueDate: string): Promise<Payment> => {
  const { data, error } = await supabase
    .from('payments')
    .update({
      due_date: dueDate,
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId)
    .select()
    .single();
    
  if (error) throw error;
  return data as Payment;
};

export const verifyPaymentStatus = async (sessionId: string): Promise<void> => {
  const { error } = await supabase.functions.invoke('verify-payment-status', {
    body: { session_id: sessionId }
  });
  
  if (error) throw error;
};

export const cancelAutomaticPayments = async (reservationId: string): Promise<void> => {
  // 1. Annuler l'abonnement de paiement
  const { error: subscriptionError } = await supabase
    .from('payment_subscriptions')
    .update({
      status: 'cancelled',
      updated_at: new Date().toISOString()
    })
    .eq('reservation_id', reservationId)
    .eq('status', 'active');
    
  if (subscriptionError) throw subscriptionError;
  
  // 2. Annuler tous les paiements automatiques en attente
  const { error: paymentsError } = await supabase
    .from('payments')
    .update({
      status: 'cancelled',
      is_automatic: false,
      notes: 'Prélèvement automatique annulé par l\'administrateur',
      updated_at: new Date().toISOString()
    })
    .eq('reservation_id', reservationId)
    .eq('is_automatic', true)
    .eq('status', 'pending');
    
  if (paymentsError) throw paymentsError;
};

export const deletePayment = async (paymentId: string): Promise<void> => {
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', paymentId);
    
  if (error) throw error;
};
